import { useEffect, useRef, useState, useMemo, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Button from 'components/Button';
import Container from 'components/Container';
import CourseCard from 'pages/PersonalArea/CourseCard';
import Modal from 'components/Modal';
import Notification from 'components/Notification';
import Tooltip from 'components/Tooltip';

import { useAppContext } from 'services/context';
import { useSessionStorage, useToggle, useTranslation } from 'services/hooks';

import { COURSES_ACTIONS, USER_ACTIONS } from 'services/reducers/actionTypes';
import { ROUTES, COURSE_TYPES, VALIDATIONS, INPUT_TYPES, NOTIFICATION_TYPES } from 'constants/index';

import UserPreferences from './UserPreferences';

import Icon from 'components/Icon';
import Input from 'components/Input';

import Spinner from 'components/Spinner';

import './style.css';

const API_URL = process.env.REACT_APP_API_URL;

const EDIT_TYPES = Object.freeze({
	DETAILS: 'details',
	PASSWORD: 'password',
});

const MY_INFO_STATUS_TYPES = Object.freeze({
	TREATED: 1,
	PENDING: 2,
	TREATED_APPROVED_DIDNT_WATCHED: 3,
	TREATED_REJECTED_DIDNT_WATCHED: 4,
});

const TABLET_SIZE = process.env.REACT_APP_TABLET_SIZE;

const TRANSLATION_KEY = 'personal_area';
const VALIDATIONS_TRANSLATION_KEY = 'validations';

const PersonalArea = () => {
	const {
		user,
		Api,
		courses,
		dispatchCourses,
		dispatchUser,
		showResponseMessagePopup,
		responseMessage,
		setResponseMessage,
		addNotification,
	} = useAppContext();
	const [cacheUser, setCacheUser] = useSessionStorage('user', '');
	const navigate = useNavigate();

	const getTranslation = useTranslation();

	const toolTipRef = useRef(null);

	const [recordId, setRecordId] = useState(undefined);
	const [disableMyInfoFormSend, setDisableMyInfoFormSend] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const [modalVisible, setModalVisible] = useToggle(false);
	const [selectedCourse, setSelectedCourse] = useState(undefined);
	const [dynamicParams, setDynamicParams] = useState([]);

	const [popupBeforeCertificateModal, setPopupBeforeCertificateModalToggle] = useToggle(false);
	const [certificateModal, setCertificateModalToggle] = useToggle(false);
	const [certificatePdf, setCertificatePdf] = useState(undefined);
	const [pdfBlob, setPdfBlob] = useState(undefined);
	const [logoutModal, setLogoutModalToggle] = useToggle(false);

	const [editMode, setEditMode] = useState(false);
	const [editKey, setEditKey] = useState(EDIT_TYPES.DETAILS);

	const [formValidationError, setFormValidationError] = useState(false);
	const [myInfoErrorMessage, setMyInfoErrorMessage] = useState('');
	const [myInfoStatusMessage, setMyInfoStatusMessage] = useState('');

	const formRef = useRef();

	// const activationCode = useMemo(() => {
	// 	return selectedCourse?.studentCourseActivationCode || 'Loading....';
	// }, [selectedCourse]);

	const userCourses = useMemo(() => {
		if (!courses) return [];
		return courses.courses;
	}, [courses]);

	//extract form status from first course founded
	const myInfoFormStatus = useMemo(() => {
		if (!userCourses) return -1;
		const _course = userCourses?.find(course => {
			return course;
		});
		if (!_course) return -1;
		return _course.myCourseUpdateFormStatus;
	}, [userCourses]);

	//navigate on new tab to whatsapp
	const redirectToWhatsApp = (e, number) => {
		window.open(`https://wa.me/${number.content}`, '_blank');
	};

	//contains all administationContact right box
	// const administationContact = [
	// 	{
	// 		id: 1,
	// 		group: 'administationContact',
	// 		name: 'whatsApp',
	// 		title: 'Contact us by WhatsApp',
	// 		iconName: 'whatsAppIcon',
	// 		iconColor: '#45c153',
	// 		content: '447723528081',
	// 		onClick: redirectToWhatsApp,
	// 	},
	// 	{
	// 		id: 2,
	// 		group: 'administationContact',
	// 		name: 'email',
	// 		title: 'Send us an Email',
	// 		iconColor: '#d41028',
	// 		iconName: 'emailIcon',
	// 		onClick: e => {
	// 			e?.preventDefault();
	// 			const supportEmail = dynamicParams.find(param => param.paramName === 'CsrEmail');
	// 			if (!supportEmail) return;

	// 			window.location = `mailto:${supportEmail.paramValue}`;
	// 		},
	// 	},
	// 	{
	// 		id: 3,
	// 		group: 'administationContact',
	// 		name: 'phone',
	// 		iconColor: '#2583ff',
	// 		number: '35315692414',
	// 		title: `Call us to +353-1569-24-14`,
	// 		iconName: 'phoneIcon',
	// 		onClick: e => {
	// 			e?.preventDefault();
	// 			const SitePhone = dynamicParams.find(param => param.paramName === 'SitePhone');
	// 			if (!SitePhone) return;

	// 			window.location = `tel:${SitePhone.paramValue}`;
	// 		},
	// 	},
	// ];

	useEffect(() => {
		async function fetchMyApi() {
			if (!user) return;
			const userCourses = await Api.GetUserCourses();
			//prevent situation when user changed is email and tab was open -> will get unautorized
			if (!userCourses) {
				setLogoutModalToggle();
				return;
			}
			if (userCourses && userCourses.length > 0) {
				dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
			}
			//fetch parameters for administrationContact
			// const _dynamicParams = await Api.GetDynamicParams();
			// if (_dynamicParams && _dynamicParams.length > 0) setDynamicParams(_dynamicParams);

			//check for changed details status of the user
			const myInfoStatus =
				userCourses?.find(course => course.myCourseUpdateFormStatus === MY_INFO_STATUS_TYPES.TREATED_APPROVED_DIDNT_WATCHED) || false;
			const updatedUserData = await Api.GetUserDetailsByUserId(user.userId);
			if (Object.keys(myInfoStatus).length > 0 && Object.keys(updatedUserData).length > 0) {
				const updatedObj = {
					...cacheUser,
					fname: updatedUserData.firstName,
					lname: updatedUserData.lastName,
					email: updatedUserData.email,
					mainPhoneNumber: updatedUserData.phoneNumber,
				};

				dispatchUser({ type: USER_ACTIONS.UPDATE_USER_DETAILS, payload: updatedObj });
				setCacheUser(updatedObj);
				setEditKey(null);
				setTimeout(() => setEditKey(EDIT_TYPES.DETAILS), 1500);
			}
		}
		fetchMyApi();
	}, []);

	//set the client block if already have pending update request
	useEffect(() => {
		if (myInfoFormStatus <= 0) return;
		if (myInfoFormStatus === MY_INFO_STATUS_TYPES.PENDING) {
			// blocked - pending change form
			setMyInfoStatusMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_pending_message`));
			setDisableMyInfoFormSend(true);
			return;
		}
		if (
			myInfoFormStatus === MY_INFO_STATUS_TYPES.TREATED_APPROVED_DIDNT_WATCHED ||
			myInfoFormStatus === MY_INFO_STATUS_TYPES.TREATED_REJECTED_DIDNT_WATCHED
		) {
			//success and not read yet
			if (myInfoFormStatus === MY_INFO_STATUS_TYPES.TREATED_APPROVED_DIDNT_WATCHED) {
				setMyInfoStatusMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_approved_didnt_watched_message`));
			}
			// else {
			// 	//rejected
			// 	setMyInfoStatusMessage('Your request is been rejected');
			// }
			_handleMyInfoUpdateWatchedStatus();
			setDisableMyInfoFormSend(false);
			return;
		}
		// MY_INFO_STATUS_TYPES.TREATED;
		setDisableMyInfoFormSend(false);
		setMyInfoStatusMessage('');
	}, [myInfoFormStatus]);

	const _handleMyInfoUpdateWatchedStatus = async () => {
		await Api.MyCourseFormUpdateIsWatched();
	};

	const _handleCourseCardClick = async ({ recordId, isInternalCourse, courseTypeId }) => {
		let currentCourse = courses.courses.find(course => course.recordId === recordId);
		if (!currentCourse) return;
		if (
			isInternalCourse ||
			courseTypeId === COURSE_TYPES.IELTS_GENERAL ||
			courseTypeId === COURSE_TYPES.TEF ||
			courseTypeId === COURSE_TYPES.TOFEL
		) {
			dispatchCourses({ type: COURSES_ACTIONS.SET_CURRENT_COURSE, payload: currentCourse });
			navigate(`/${ROUTES.COURSE}/${recordId}`);
			return;
		}
		// Navigate to MyCourse Platform -> ESL and IELTS Premium Only
		if (courseTypeId === COURSE_TYPES.ESL || courseTypeId === COURSE_TYPES.IELTS) {
			// const { studentCourseActivationCode } = currentCourse;
			// if (!studentCourseActivationCode) {
			// 	currentCourse = await Api.GetActivationCode(recordId);
			// 	const userCourses = await Api.GetUserCourses();
			// 	if (userCourses?.length > 0) {
			// 		dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
			// 	}
			// }

			dispatchCourses({ type: COURSES_ACTIONS.SET_CURRENT_COURSE, payload: { ...currentCourse } });
			setSelectedCourse({ ...currentCourse });
			navigate(`${ROUTES.MY_COURSE}/${recordId}`);
			// setModalVisible(true);
		}
	};

	const getCourseProgress = course => {
		return course.courseTypeId === COURSE_TYPES.ESL || course.courseTypeId === COURSE_TYPES.IELTS
			? course.myCourseRateOfProgress
			: course.rateOfProgress;
	};

	const _renderCourseCard = course => {
		const isInternal = course.courseTypeId === COURSE_TYPES.INTERNAL;
		//if its myCourse - will be controlled by myCourseRateOfProgress and not by rateOfProgress
		const _progress = getCourseProgress(course);
		return (
			<CourseCard
				onClick={_handleCourseCardClick}
				recordId={course.recordId}
				key={course.recordId}
				progress={_progress}
				title={course.courseName}
				thumbnail={course.coursePhotoThumbnailURL}
				disabled={course.disabled}
				isInternalCourse={isInternal}
				courseTypeId={course.courseTypeId}
				studentCourseActivationCode={course.studentCourseActivationCode}
				courseExternalURL={course.courseExternalURL}
				introductionFileURL={course.introductionFileURL}
			/>
		);
	};

	const _copyToClipboard = (value, e) => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
			toolTipRef.current.Show({ x: e.clientX, y: e.clientY });
		}
	};

	const _openExternalResource = async () => {
		try {
			if (!selectedCourse) return;
			const { courseExternalURL } = selectedCourse;
			if (courseExternalURL) window.open(courseExternalURL, '_blank', 'noopener,noreferrer');
		} catch (error) {
			console.warn('_openExternalResource', error);
		}
	};

	const INSTRUCTIONS_URL = API_URL + (selectedCourse?.introductionFileURL || '').replace(/\\/g, '/');

	//////////////// extented PersonalArea

	//navigate to course review page - for btn on completed screen
	const redirectToReview = recordId => {
		if (!recordId) return;
		navigate(`${ROUTES.REVIEW}/${recordId}`);
	};

	//handles reset password request on My Info section
	const resetPassword = async (newPassword, confirmPassword) => {
		setFormValidationError(false);
		try {
			// also here to prevent user from sending empty
			if (newPassword?.length !== 8 || !INPUT_TYPES.PASSWORD_STRENGTH.regex.test(newPassword)) {
				setMyInfoErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_validation_error_message`));
				setFormValidationError(true);
				return false;
			}

			if (newPassword?.trim() !== confirmPassword?.trim()) {
				setMyInfoErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_match_error_message`));
				setFormValidationError(true);
				return false;
			}

			const res = await Api.UpdateUserPassword(user.userId, newPassword);
			if (!res) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				return false;
			}

			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);

			const token = Api.attachAuth(user.email, newPassword);
			setCacheUser({ ...cacheUser, token });
			return true;
		} catch (err) {
			console.error(err);

			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
		}
	};

	//handles onSubmit My info
	const _handleMyInfoUpdateDetailsOrPassword = async e => {
		e?.preventDefault();
		setFormValidationError(false);
		const form = new FormData(formRef.current);
		const formData = Object.fromEntries(form.entries());
		if (!formData) return;
		Object.entries(formData).forEach(([key]) => (formData[key] = formData[key].trim())); //Remove spaces.
		//case for details update form
		if (editKey === EDIT_TYPES.DETAILS) {
			if (!VALIDATIONS.EMAIL.regex.test(formData['email'])) {
				setFormValidationError(true);
				setMyInfoErrorMessage(
					getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.EMAIL.errorTranslationKey) || VALIDATIONS.EMAIL.error
				);
				return;
			}
			//validation prevent text on phone
			if (!VALIDATIONS.POSITIVE_NUMBER.regex.test(formData['phone'])) {
				setFormValidationError(true);
				setMyInfoErrorMessage(
					getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.POSITIVE_NUMBER.errorTranslationKey) || VALIDATIONS.POSITIVE_NUMBER.error
				);
				return;
			}
			for (const key in formData) {
				//validation prevent null values
				if (!formData[key]) {
					setFormValidationError(true);
					setMyInfoErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_fields_validation_message`));
					return;
				}
				//validation prevent numbers on firstname and lastName
				if (!VALIDATIONS.ALPHABETIC_WITH_SPACE.regex.test(formData[key]) && key !== 'phone' && key !== 'email') {
					setFormValidationError(true);
					setMyInfoErrorMessage(
						getTranslation(VALIDATIONS_TRANSLATION_KEY, VALIDATIONS.ALPHABETIC_WITH_SPACE.errorTranslationKey) ||
							VALIDATIONS.ALPHABETIC_WITH_SPACE.error
					);
					return;
				}
			}

			const res = await Api.SaveMyCourseFormUpdate(formData);
			if (res.isFormSaved) {
				setEditKey(EDIT_TYPES.DETAILS);
				setDisableMyInfoFormSend(true);
				setMyInfoStatusMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_pending_message`));
				setEditMode(false);
				return;
			}
			setFormValidationError(true);
			setMyInfoErrorMessage(getTranslation(TRANSLATION_KEY, res.message));
			return;
		}
		//case for password reset
		const { new_password, confirm_password } = formData;
		const passwordReset = await resetPassword(new_password, confirm_password);
		if (!passwordReset) {
			return;
		}
		setEditMode(false);
		setEditKey(EDIT_TYPES.DETAILS);
	};

	const _handleChangeEditKeyState = e => {
		e?.preventDefault();
		setEditMode(true);
		setMyInfoErrorMessage('');
		setMyInfoStatusMessage('');
		setEditKey(prev => (prev === EDIT_TYPES.PASSWORD ? EDIT_TYPES.DETAILS : EDIT_TYPES.PASSWORD));
	};

	const _handleCancelBtnOnClick = e => {
		e.preventDefault();
		setMyInfoErrorMessage('');
		setMyInfoStatusMessage('');
		setEditMode(false);
		if (editKey === EDIT_TYPES.PASSWORD) {
			setEditKey(EDIT_TYPES.DETAILS);
		}
	};

	const _handleEditDetailsClick = e => {
		e.preventDefault();
		setEditMode(prev => !prev);
		setMyInfoErrorMessage('');
		setMyInfoStatusMessage('');
		setIsDirty(false);
	};

	//generate certificate
	const _handleCertificate = async () => {
		if (!recordId) return;

		const _pdfBlob = await Api.GetCertificate(recordId);
		const _pngBlob = await Api.GetCertificatePng(recordId);
		if (!_pdfBlob || !_pngBlob) return;

		const reader = new FileReader();
		reader.onloadend = function () {
			const base64data = reader.result;
			setCertificatePdf(base64data);
		};

		reader.readAsDataURL(_pngBlob);
		setPdfBlob(_pdfBlob);
		setCertificateModalToggle();
		if (popupBeforeCertificateModal) setPopupBeforeCertificateModalToggle();
	};

	//handle show pdf on pop up
	const _handleViewPdf = async course => {
		if (!course) return;
		if (course.recordId) setRecordId(course.recordId);

		//check if the user already filled the review in the same course
		const questions = await Api.GetReviewQuestions(course.recordId);
		if (!questions) return;
		if (!questions?.isQuestionnaireExists) {
			setPopupBeforeCertificateModalToggle();
			return;
		}
		_handleCertificate(course.recordId);
	};

	//handle pdf download on certificate popup
	const _handleDownLoadPdf = () => {
		if (!pdfBlob) return;
		// const courseName = currentCourse?.courseName || '';
		const link = document.createElement('a');
		link.href = URL.createObjectURL(pdfBlob); //create a blobURI pointing to our Blob
		// link.download = `${courseName} Certificate`; // some browser needs the anchor to be in the doc
		link.download = `Certificate`; // some browser needs the anchor to be in the doc
		link.click();
		setTimeout(() => URL.revokeObjectURL(link.href), 1000); // in case the Blob uses a lot of memory
	};

	//if the user clicked on EDIT DETAILS and didnt change info it will stop him from send request
	const _handleOnChangeInputs = () => {
		setIsDirty(true);
		setMyInfoStatusMessage('');
		setMyInfoErrorMessage('');
		const form = new FormData(formRef.current);
		const formData = Object.fromEntries(form.entries());
		if (!formData) return;
		const originalUserData = {
			first_name: user.fname,
			last_name: user.lname,
			phone: user.mainPhoneNumber,
			email: user.email,
		};

		const unChangedData = Object.keys(formData).some(key => formData[key] !== originalUserData[key]);
		if (!unChangedData || (formData.confirm_password === '' && formData.new_password === '')) {
			setIsDirty(false);
			return;
		}
		//will work for empty values also
		if (editKey === EDIT_TYPES.PASSWORD && !INPUT_TYPES.PASSWORD_STRENGTH.regex.test(formData.new_password)) {
			setMyInfoErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reset_validation_error_message`));
			setFormValidationError(true);
			return false;
		}
	};

	//returns array with the user completed courses
	const userCompletedCourses = useMemo(() => {
		return userCourses.filter(course => parseInt(getCourseProgress(course)) >= 100);
	}, [userCourses]);

	//handles the render of MyInfo section Inputs
	const renderSectionByEditKey = useMemo(() => {
		if (!editKey) return <Spinner visible />;
		switch (editKey) {
			case EDIT_TYPES.DETAILS:
				return (
					<div className="MyInfo__content__details">
						<Input
							key="first_name"
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_details_first_name_field`)}
							name="first_name"
							defaultValue={user.fname}
							disabled={!editMode}
							onChange={_handleOnChangeInputs}
						/>
						<Input
							key="last_name"
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_details_last_name_field`)}
							name="last_name"
							defaultValue={user.lname}
							disabled={!editMode}
							onChange={_handleOnChangeInputs}
						/>
						<Input
							key="phone"
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_details_phone_field`)}
							name="phone"
							defaultValue={user.mainPhoneNumber}
							disabled={!editMode}
							onChange={_handleOnChangeInputs}
						/>
						<Input
							key="email"
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_details_email_field`)}
							name="email"
							defaultValue={user.email}
							disabled={!editMode}
							onChange={_handleOnChangeInputs}
						/>
					</div>
				);
			case EDIT_TYPES.PASSWORD:
				return (
					<div className="MyInfo__content__password">
						<Input
							key="new_password"
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_password_field`)}
							name="new_password"
							disabled={!editMode}
							onChange={_handleOnChangeInputs}
						/>
						<Input
							key="confirm_password"
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_password_confirm_field`)}
							name="confirm_password"
							disabled={!editMode}
							onChange={_handleOnChangeInputs}
						/>
					</div>
				);
			default:
				return;
		}
	}, [user, myInfoFormStatus, editKey, editMode, getTranslation]);

	//handles the render of MyInfo section buttons
	const renderBtnsByEditKey = useMemo(() => {
		if (editMode) {
			return (
				<>
					<Button
						small
						title={
							editKey === EDIT_TYPES.PASSWORD
								? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_update_button`)
								: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_send_button`)
						}
						onClick={_handleMyInfoUpdateDetailsOrPassword}
						disabled={!isDirty}
					/>

					<Button
						small
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_cancel_button`)}
						onClick={_handleCancelBtnOnClick}
					/>
				</>
			);
		}
		return (
			<>
				<Button
					small
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_edit_button`)}
					onClick={_handleEditDetailsClick}
					disabled={disableMyInfoFormSend}
				/>
				<Button
					small
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_change_button`)}
					extraClassName="change_password"
					onClick={_handleChangeEditKeyState}
					// disabled={disableMyInfoFormSend}
				/>
			</>
		);
	}, [editKey, editMode, disableMyInfoFormSend, isDirty, getTranslation]);

	//renders the right icon to status
	const renderIconByStatus = useMemo(() => {
		if (
			myInfoFormStatus === MY_INFO_STATUS_TYPES.PENDING ||
			(myInfoFormStatus === MY_INFO_STATUS_TYPES.TREATED_REJECTED_DIDNT_WATCHED && disableMyInfoFormSend)
		) {
			return <Icon icon="clockIcon" />;
		}

		if (myInfoFormStatus === MY_INFO_STATUS_TYPES.TREATED_APPROVED_DIDNT_WATCHED) return <Icon icon="doneIcon" />;
		return null;
	}, [myInfoFormStatus, disableMyInfoFormSend]);

	const filteredCourses = useMemo(() => {
		if (!courses?.courses) return;
		const _filteredCourses = courses?.courses?.filter(course => !course.disabled && !course.courseStatusClassName !== 'Cancelled');
		return _filteredCourses;
	}, [courses?.courses]);

	const itemsPerRow = userCourses.length % 2 === 0 ? 4 : 3;

	const boxWrapperClassName = classNames('box__wrapper', {
		[`N_${itemsPerRow}`]: itemsPerRow,
	});

	return (
		<div>
			<div className="PersonalArea">
				<Container>
					<div className="PersonalArea__top_container">
						<div className="PersonalArea__top_container__my_info">
							<div className="MyInfo">
								<div className="MyInfo__title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_info_title`)}</div>
								<div className="MyInfo__content">
									<form id="form" ref={formRef} onSubmit={_handleMyInfoUpdateDetailsOrPassword}>
										{myInfoStatusMessage && (
											<div className="MyInfo__status">
												{renderIconByStatus}
												<div className="MyInfo__status__message" dangerouslySetInnerHTML={{ __html: myInfoStatusMessage }}></div>
											</div>
										)}
										{renderSectionByEditKey}
										{formValidationError && myInfoErrorMessage && (
											<div className="error_message" dangerouslySetInnerHTML={{ __html: myInfoErrorMessage }}></div>
										)}
										<div className="MyInfo__btn_wrapper">{renderBtnsByEditKey}</div>
									</form>
								</div>
							</div>
						</div>
						{/* <div className="PersonalArea__top_container__administration">
							<Box
								headTitle="Administration"
								content={administationContact}
								leftIcons
								iconSize={22}
								renderItem={(item, { iconSize, leftIcons }) => {
									const { title, id, name, iconName, iconColor, group, onClick, renderAccessCode, copyToClipboard } = item;

									const onClickCheck = e => onClick && typeof onClick === 'function' && onClick(e, item);
									return (
										<div className={`item_wrapper ${group}__${name}`} key={id}>
											<div className={`item_wrapper__title`} onClick={e => onClickCheck(e, item)}>
												{renderAccessCode}
												{copyToClipboard}
												{leftIcons && <Icon icon={iconName} color={iconColor} size={iconSize} onClick={e => onClickCheck(e, item)} />}
												{title}
												{!copyToClipboard && !leftIcons && <Icon icon={iconName} color={iconColor} size={iconSize} />}
											</div>
										</div>
									);
								}}
							/>
						</div> */}
						<div className="PersonalArea__top_container__user_preferences">
							<UserPreferences />
						</div>
					</div>
					<div className="PersonalArea__my_courses">
						<div className="PersonalArea__main_title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_main_title`)}</div>
						{(filteredCourses?.length && <div className="PersonalArea__grid">{filteredCourses?.map(_renderCourseCard)}</div>) ||
							getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_no_results`)}
					</div>
					<div className="PersonalArea__my_certificates">
						<div className="PersonalArea__my_certificates__title">
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_my_certificates_title`)}
						</div>
						<div className="completed_courses">
							<div className="completed_courses__grid">
								<div className="completed_courses__title">
									{user.fname}, {getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_courses_title`)}{' '}
									<span className="red">
										{' '}
										{userCompletedCourses.length}/{userCourses.length}{' '}
									</span>{' '}
									{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_courses_sub_title`)}
								</div>
								<div className="completed_courses__progress">
									<div className={boxWrapperClassName}>
										{userCourses
											.sort((a, b) => getCourseProgress(b) - getCourseProgress(a))
											.map((course, idx) => {
												// const itemsPerRow = userCourses.length % 2 === 0 ? 4 : 3;
												const newRow = (idx + 1) % itemsPerRow == 0;

												const isLast = idx === userCourses.length - 1;
												const isCompleted = parseInt(getCourseProgress(course)) >= 100;
												const className = classNames('progress_box', {
													last: isLast && !isCompleted && idx !== 0,
													completed: isCompleted,
													next: idx === userCompletedCourses.length,
													['last_in_row']: newRow && !isLast,
												});
												return (
													<div key={idx}>
														<div className={className} idx={idx}>
															{!isLast && <div className="before"></div>}
														</div>
													</div>
												);
											})}
									</div>
								</div>
							</div>
						</div>

						<div className="certificates_view">
							{userCourses &&
								userCourses.map((course, idx) => {
									//if its myCourse - will be controlled by myCourseRateOfProgress and not by rateOfProgress
									const _progress = getCourseProgress(course);
									return (
										<CertificateCard
											key={idx}
											courseName={course.courseName}
											courseStatus={course.studentCourseStatusName}
											rateOfProgress={_progress}
											disabled={_progress < 100 || (!course.disabled && !course.hasCompletedCourse)}
											isInternalCourse={course.courseTypeId === COURSE_TYPES.INTERNAL}
											onClick={() => _handleViewPdf(course)}
										/>
									);
								})}
						</div>
					</div>
				</Container>
			</div>
			<Modal isVisible={modalVisible} setVisibility={setModalVisible} extraClassName="activation_code_modal" dropInAnimation>
				<div>
					<div dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_modal_text`) }}></div>
					<div className="logo"></div>
				</div>
				{/* <div className="paragraph">
					Dear student, before going to the course page,&nbsp;&nbsp;
					<a href={INSTRUCTIONS_URL} target="_blank" download>
						read the instructions.
					</a>
				</div>
				<div className="paragraph">
					To view your code, click here
					<Button
						title={activationCode || 'Loading...'}
						small
						extraClassName="activation_code_button"
						onClick={e => _copyToClipboard(activationCode || 'NOT FOUND', e)}
					/>
				</div>
				<Button
					title="Continue to Education Platform"
					small
					extraClassName="continue_button"
					endIcon="rightArrow"
					onClick={_openExternalResource}
				/> */}
			</Modal>
			<Modal
				extraClassName="certificate_modal"
				isVisible={popupBeforeCertificateModal}
				setVisibility={setPopupBeforeCertificateModalToggle}
			>
				<div className="certificate_modal__content">
					<div
						className="certificate_modal__content__title"
						dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_text`) }}
					></div>
					<div className="btn_wrapper">
						<Button
							extraClassName="continue"
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_continue_button`)}
							onClick={() => redirectToReview(recordId)}
						/>
						<Button
							extraClassName="not_now"
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_not_now_button`)}
							onClick={_handleCertificate}
						/>
					</div>
				</div>
			</Modal>
			<Modal isVisible={certificateModal} setVisibility={setCertificateModalToggle} extraClassName="certificate_pdf">
				<div>
					<img className="certificate_pdf__img" src={`${certificatePdf}`} />
				</div>
				<Button
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_download_button`)}
					onClick={_handleDownLoadPdf}
					endIcon="downloadIcon"
					small
				/>
			</Modal>

			<Tooltip ref={toolTipRef}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_tooltip_note`)}</Tooltip>
			<Modal isVisible={logoutModal} setVisibility={setLogoutModalToggle} extraClassName="logout_popup" hideCloseButton>
				<div
					className="logout_popup__title"
					dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_logout_modal_text`) }}
				></div>
				<Button
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_logout_modal_button`)}
					onClick={() => {
						dispatchUser({ type: USER_ACTIONS.LOG_OUT });
						setCacheUser({});
					}}
					small
				/>
			</Modal>
		</div>
	);
};

export default PersonalArea;

export const CertificateCard = ({ courseName, rateOfProgress, disabled, onClick }) => {
	const getTranslation = useTranslation();

	const courseStatusClassName = useMemo(() => {
		if (parseInt(rateOfProgress) >= 0 && parseInt(rateOfProgress) < 100) {
			return 'in_progress';
		} else if (parseInt(rateOfProgress) >= 100) {
			return 'completed';
		}
	}, [rateOfProgress]);

	const onClickCard = () => {
		onClick && typeof onClick === 'function' && onClick();
	};

	const CourseState = useMemo(() => {
		if (parseInt(rateOfProgress) <= 0) return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_state_not_started`);
		else if (parseInt(rateOfProgress) >= 100) return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_state_completed`);
		return getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_state_in_progress`);
	}, [rateOfProgress, getTranslation]);

	return (
		<div className={`CertificateCard ${courseStatusClassName}`} onClick={!disabled ? onClickCard : undefined}>
			<div className="CertificateCard__preview">
				<div className="CertificateCard__preview__img_container">
					<div className={`CertificateCard__preview__img ${disabled ? 'disabled' : ''}`}></div>
				</div>
			</div>
			<div className={`CertificateCard__course ${disabled ? '--disabled' : ''}`}>
				<div className="CertificateCard__course__title">{courseName}</div>
				<div className={`CertificateCard__course__status ${courseStatusClassName}`}>{CourseState}</div>
				{rateOfProgress && rateOfProgress >= 100 ? (
					<>
						<div className="CertificateCard__course__show">
							<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_show_button`)} endIcon="rightArrow" />
							{/* <CertificateProgressBar rateOfProgress={rateOfProgress} /> */}
						</div>
					</>
				) : null}
			</div>
		</div>
	);
};

export const CertificateProgressBar = ({ rateOfProgress }) => {
	const completed = parseInt(rateOfProgress);

	return (
		<div className="CertificateProgressBar">
			<div className="CertificateProgressBar__precentage">{rateOfProgress}%</div>
			<div className="CertificateProgressBar__progress_wrapper">
				<div className="CertificateProgressBar__progress_wrapper__bar"></div>
				<div className="CertificateProgressBar__progress_wrapper__bar--completed" width={completed} style={{ width: completed }}></div>
			</div>
		</div>
	);
};
